import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import { usePermissions } from '../../../utils/usePermissions';
import { ConfirmDialog, EditEmptyState } from '../reports/edit/EditCommon';
import { ACTION_BUTTON_SX } from '../reports/summary/summaryStyles';
import FieldFormDialog from './fields/FieldFormDialog';
import FieldsTable from './fields/FieldsTable';
import { MATH_FIELDS_API, MATH_FIELDS_PERMS } from './fields/fieldsApi';
import type { FieldsApi, FieldsPerms } from './fields/fieldsApi';
import { filterFields } from './fields/fieldsCalc';
import { useMathFields } from './fields/useMathFields';
import { PRIMARY_BUTTON_SX } from './mathTableStyles';
import type { MathColumn } from './report/mathReportApi';

const BORDER = '1px solid #e2e8f0';

/** Форма и диалог удаления остаются смонтированными на время закрытия, чтобы текст не пропадал посреди анимации */
interface FormState {
  key: number;
  target: MathColumn | null;
  open: boolean;
}

interface DeleteState {
  field: MathColumn;
  open: boolean;
}

interface MathReportFieldsProps {
  /** Вызовы справочника; по умолчанию - вкладка «Мат. отчет - Отображение». Должны быть константой модуля */
  api?: FieldsApi;
  /** Коды прав на действия; по умолчанию - права вкладки «Отображение» */
  perms?: FieldsPerms;
}

const MathReportFields: React.FC<MathReportFieldsProps> = ({ api = MATH_FIELDS_API, perms = MATH_FIELDS_PERMS }) => {
  const { hasPerm } = usePermissions();
  const canCreate = hasPerm(perms.create);
  const canEdit = hasPerm(perms.edit);
  const canDelete = hasPerm(perms.delete);

  const { fields, loading, error, retry, pending, saveField, removeField } = useMathFields(api);

  const [query, setQuery] = useState('');
  const [form, setForm] = useState<FormState | null>(null);
  const [removal, setRemoval] = useState<DeleteState | null>(null);

  const filtered = useMemo(() => (fields ? filterFields(fields, query) : []), [fields, query]);

  const handleQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value), []);

  // Каждое открытие формы получает новый key: поля заполняются заново, а не хранят прежний ввод
  const openForm = useCallback(
    (target: MathColumn | null) => setForm((prev) => ({ key: (prev ? prev.key : 0) + 1, target, open: true })),
    []
  );
  const handleCreate = useCallback(() => openForm(null), [openForm]);
  const closeForm = useCallback(() => setForm((prev) => prev && { ...prev, open: false }), []);

  const handleAskDelete = useCallback((field: MathColumn) => setRemoval({ field, open: true }), []);
  const closeRemoval = useCallback(() => setRemoval((prev) => prev && { ...prev, open: false }), []);

  const handleConfirmDelete = useCallback(async () => {
    if (!removal) return;
    if (await removeField(removal.field)) closeRemoval();
  }, [removal, removeField, closeRemoval]);

  const renderContent = () => {
    if (error && !fields) {
      return (
        <EditEmptyState
          icon={<ErrorOutlineIcon />}
          title="Не удалось загрузить поля"
          text={error}
          action={<Button variant="outlined" onClick={retry} sx={ACTION_BUTTON_SX}>Повторить</Button>}
        />
      );
    }
    if (!fields) {
      return (
        <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      );
    }
    if (fields.length === 0) {
      return (
        <EditEmptyState
          icon={<TableChartOutlinedIcon />}
          title="Поля мат. отчета не настроены"
          text={canCreate ? 'Добавьте поля, чтобы они появились колонками на вкладке «Отчет».' : undefined}
          action={
            canCreate ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={PRIMARY_BUTTON_SX}>
                Добавить поле
              </Button>
            ) : undefined
          }
        />
      );
    }
    if (filtered.length === 0) {
      return (
        <EditEmptyState
          icon={<SearchOffIcon />}
          title="Ничего не найдено"
          text="Полей с таким идентификатором или заголовком нет. Проверьте запрос."
        />
      );
    }
    return (
      <Paper
        elevation={0}
        sx={{ border: BORDER, borderRadius: '12px', overflow: 'hidden', transition: 'opacity 0.15s ease' }}
        style={{ opacity: loading ? 0.55 : 1 }}
      >
        <FieldsTable fields={filtered} canEdit={canEdit} canDelete={canDelete} onEdit={openForm} onDelete={handleAskDelete} />
      </Paper>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, maxWidth: 480 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Поиск по идентификатору или заголовку"
            value={query}
            onChange={handleQueryChange}
            disabled={!fields}
            sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff', borderRadius: '8px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                </InputAdornment>
              )
            }}
          />
          {fields && (
            <Typography variant="body2" sx={{ color: '#64748b', whiteSpace: 'nowrap' }}>
              Всего: <b>{filtered.length}</b>
            </Typography>
          )}
        </Box>

        {canCreate && fields && fields.length > 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate} sx={PRIMARY_BUTTON_SX}>
            Добавить поле
          </Button>
        )}
      </Box>

      {renderContent()}

      {form && fields && (
        <FieldFormDialog
          key={form.key}
          open={form.open}
          target={form.target}
          fields={fields}
          saving={pending === 'save'}
          onSave={saveField}
          onClose={closeForm}
        />
      )}

      <ConfirmDialog
        open={!!removal && removal.open}
        title="Удалить поле"
        confirmText="Удалить"
        confirmColor="error"
        loading={pending === 'delete'}
        maxWidth="xs"
        onConfirm={handleConfirmDelete}
        onClose={closeRemoval}
      >
        {removal && (
          <>
            <Typography sx={{ color: '#334155', fontSize: '0.92rem', mb: 1 }}>
              Удалить поле <b>«{removal.field.headerName}»</b> (<code>{removal.field.field}</code>)?
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.86rem' }}>
              Колонка пропадет из таблицы мат. отчета. Данные в записях по аптекам не удаляются.
            </Typography>
          </>
        )}
      </ConfirmDialog>
    </Box>
  );
};

export default MathReportFields;
